import type { MVPYearlyMileage_Top } from 'gw-edge-client/dist/types/dto/pc/typekey';
import type { OpcCoverableAttribute } from '../../../types';
import { Grid, GridItem, Link, NumberField, SelectField } from '@flos/react-ui';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useRef, useState } from 'react';
import { useHippoText } from '../../../../../hooks/useHippoText';
import { isOpcDataFilledAtom } from '../../../../../recoil/atoms/isOpcDataFilledAtom';
import { useYearlyMileagesAndOdometerReading } from '../../../../../hooks/useYearlyMileagesAndOdometerReading';
import { isOpcFetchingAtom } from '../../../../../recoil/atoms/isOpcFetchingAtom';
import CalculatorModal from '../../../../CoveragePackagePage/components/CoveragePackageInputs/CalculatorModal';
import { getOPCReceiptPageChangeTypeAtom } from '../../../../../recoil/atoms/getOPCReceiptPageChangeTypeAtom';
import { carOPCYearlyMileageStepAtom } from '../../../../../recoil/atoms/carOPCYearlyMileageStepAtom';
import { jobAtom } from '../../../../../recoil/atoms/jobAtom';

enum ODOMETER_READING_ERROR {
    EMPTY_ODOMETER = 'Empty Odometer reading',
    INT_PARSE = 'Cannot parse odometer reading to Integer',
    REQUIRE_NEW_ODOMETER = 'New yearly mileage require new odometer reading',
    NEW_ODOMETER_LOWER_THAN_PREVIOUSLY = 'New odometer reading must be higher than current odometer reading'
}

const CarYearlyMileageChange = ({
    updateCoverableAttribute
}: {
    updateCoverableAttribute: (attribute: OpcCoverableAttribute) => void;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const isOpcFetching = useRecoilValue(isOpcFetchingAtom);

    const [odometerError, setOdometerError] = useState<string>('');
    const [showModal, setShowModal] = useState(false);
    const { options, suggestedMileage, setSuggestedMileage, updateAdditionalAttributeOPC } =
        useYearlyMileagesAndOdometerReading();
    const odometerReadingRef = useRef<HTMLInputElement>(null);
    const setIsOpcDataFilled = useSetRecoilState(isOpcDataFilledAtom);
    const setOPCReceiptPageChangeType = useSetRecoilState(getOPCReceiptPageChangeTypeAtom);
    const carOPCYearlyMileageStep = useRecoilValue(carOPCYearlyMileageStepAtom);
    const { isOpcJob } = useRecoilValue(jobAtom);

    const errorMessage: Record<string, string> = {
        [ODOMETER_READING_ERROR.EMPTY_ODOMETER]: '',
        [ODOMETER_READING_ERROR.INT_PARSE]: '',
        [ODOMETER_READING_ERROR.REQUIRE_NEW_ODOMETER]:
            'Kilometerstand skal ændres når kilometerforbrug ændres.',
        [ODOMETER_READING_ERROR.NEW_ODOMETER_LOWER_THAN_PREVIOUSLY]: getHippoText(
            'DSS-ADJUST-YEARLY-MILEAGE-ODOMETER-READING-FIELD-VALIDATION-TEXT'
        )
    };

    const handleValidateAndUpdateOdometerReading = async (
        odometerValue: string,
        yearlyMileage: MVPYearlyMileage_Top
    ): Promise<void> => {
        await updateAdditionalAttributeOPC(yearlyMileage, odometerValue).then((invalidReason) => {
            setOdometerError(invalidReason);
            if (invalidReason) {
                setIsOpcDataFilled(false);
                return;
            }
            setIsOpcDataFilled(true);
            setOPCReceiptPageChangeType('carOdometerReading');
        });
    };

    const handleYearlyMileageChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedIndex = e.target.selectedIndex;
        const { value } = e.target.options[selectedIndex];
        setSuggestedMileage(value as MVPYearlyMileage_Top);
        updateCoverableAttribute({
            yearlyMileage: value as MVPYearlyMileage_Top
        });
        setIsOpcDataFilled(true);
    };

    const handleCalculatorMileageChange = (value: MVPYearlyMileage_Top): void => {
        setSuggestedMileage(value);
        updateCoverableAttribute({
            yearlyMileage: value
        });
    };

    const handleOdometerFieldBlur = (e: React.FocusEvent<HTMLInputElement, Element>): void => {
        setOdometerError('');
        const updatedOdometerValue = e.target.value.replaceAll('.', '');

        void handleValidateAndUpdateOdometerReading(updatedOdometerValue, suggestedMileage);
    };

    const isEmptyOdometer = [
        ODOMETER_READING_ERROR.EMPTY_ODOMETER,
        ODOMETER_READING_ERROR.INT_PARSE
    ].includes(odometerError as ODOMETER_READING_ERROR);

    return (
        <>
            <Grid gap={6}>
                <GridItem span={12}>
                    {carOPCYearlyMileageStep === 'yearlyMileage' && (
                        <>
                            <h6>{getHippoText('DSS-ADJUST-YEARLY-MILEAGE-TEXT')}</h6>
                            <SelectField
                                label={getHippoText('DSS-OPC-INPUT-LABEL')}
                                options={options}
                                id="carYearlyMileage"
                                name="carYearlyMileage"
                                onChange={handleYearlyMileageChange}
                                value={suggestedMileage}
                                disabled={isOpcFetching}
                            />
                            <Link
                                theme="logo"
                                className={
                                    isOpcFetching
                                        ? 'u-text-right u-color-supplement-grey-medium'
                                        : 'u-text-right'
                                }
                                onClick={() => {
                                    if (isOpcFetching) return;
                                    setShowModal(true);
                                }}
                            >
                                {getHippoText('DSS-ACTIVATION-FLOW-YEARLY-MILEAGE-CALCULATOR')}
                            </Link>
                        </>
                    )}
                    {carOPCYearlyMileageStep === 'odometerReading' && isOpcJob && (
                        <>
                            <h6>
                                {getHippoText('DSS-ADJUST-YEARLY-MILEAGE-ODOMETER-READING-TEXT')}
                            </h6>
                            <p>{getHippoText('DSS-OPC-UPDATE-ODOMETER-SUBTITLE')}</p>
                            <NumberField
                                ref={odometerReadingRef}
                                label={getHippoText('DSS-ODOMETER-STEP-INPUT-TEXT')}
                                id="carOdometerReading"
                                name="carOdometerReading"
                                withFormatting={true}
                                isValid={!odometerError}
                                onFocus={() => {
                                    setIsOpcDataFilled(false);
                                }}
                                {...(isEmptyOdometer ? {} : { errorText: '' })}
                                onBlur={handleOdometerFieldBlur}
                                disabled={isOpcFetching}
                            />
                            {!!odometerError && !isEmptyOdometer && (
                                <p className="paragraph--small u-color-supplement-error-base">
                                    {errorMessage[odometerError]}
                                </p>
                            )}
                        </>
                    )}
                </GridItem>
            </Grid>

            {showModal && (
                <CalculatorModal
                    onCalculatorMileageChange={handleCalculatorMileageChange}
                    showModal={showModal}
                    closeModal={() => {
                        setShowModal(false);
                    }}
                />
            )}
        </>
    );
};

export default CarYearlyMileageChange;
